import { getVisualMapSettings } from 'helpers/chart/visualMap';
import { getMappedColor } from '../helpers/getColor';
import {
    CartesianChartOption,
    CartesianChartType,
} from './CartesianChartOption';
import { EChartsSerie } from './types';
import { formatDisplay } from 'helpers/formatHelper';
import { getValidSettings, plainResponseToFloatValue } from './helpers';
import { EChartsOption } from 'echarts';
import { scaterplotSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/settingsByViz/scatterplot';
import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';

export class ScatterChartOption extends CartesianChartOption {
    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        // Delete unhandled settings:
        const properSettings = getValidSettings(settings, scaterplotSettings);
        super(
            orderedColumns,
            properSettings,
            data,
            width,
            height,
            dataZoomInterval,
            isPdfModeActivated,
            isPngModeActivated,
            isChartViewedOnDashboard,
        );
    }

    protected getChartType(): CartesianChartType {
        return 'scatter';
    }

    getChartOption(): EChartsOption {
        const option = super.getChartOption();
        if (this.settings.useGradientColor) {
            const visualMap = this.getVisualMap();
            // When we show the visual map, we hide the legend.
            return {
                ...option,
                visualMap,
                legend: {
                    ...option.legend,
                    show: false,
                },
            };
        }
        return option;
    }

    protected override getSeries(): EChartsSerie[] {
        // @ts-expect-error [typing] Adding the position top breaks the typing 🤷.
        return super.getSeries().map((serie, serieIndex) => ({
            ...serie,
            itemStyle: {
                color: getMappedColor(
                    serie.name,
                    serieIndex,
                    this.getCustomColor(serie.name),
                    this.getColor(),
                ),
            },
            label: {
                ...serie.label,
                position: 'top',
            },
        }));
    }

    protected getMinMaxValue(): { min: number; max: number } {
        const valueKey = this.orderedColumns.y.key;
        let maxValue = 0;
        let minValue = Number.MAX_VALUE;
        for (const dataset of this.dataset) {
            if (!('source' in dataset)) {
                continue;
            }
            for (const data of dataset.source) {
                const yValue = data[valueKey];
                if (!yValue) {
                    continue;
                }
                const numberValue: number = plainResponseToFloatValue(yValue);
                if (numberValue > maxValue) {
                    maxValue = numberValue;
                }
                if (numberValue < minValue) {
                    minValue = numberValue;
                }
            }
        }
        return { max: maxValue, min: minValue };
    }

    protected getVisualMap() {
        const { min, max } = this.getMinMaxValue();
        return getVisualMapSettings({
            maxValue:
                this.settings.goalValue !== undefined
                    ? this.settings.goalValue
                    : max,
            minValue: min,
            useGradientColor: !!this.settings.useGradientColor,
            reverseGradientColor: !!this.settings.reverseGradientColor,
            showLegend: !!this.settings.showLegend,
            showLegendOnRightSide: !!this.settings.showLegendOnRightSide,
            palette: this.settings.palette || 'manty',
            dimensionKey: this.orderedColumns.y.key,
            useDataset: true,
            formatter: (value: number) =>
                formatDisplay(
                    value,
                    this.orderedColumns.y.type,
                    this.getDecimalPrecision(),
                    this.settings.displayBy,
                ),
        });
    }

    /**
     * For scatter plot we don't want double Y axis, so we always return only one.
     */
    protected override getYAxis(): EChartsOption['yAxis'] {
        const yAxis = super.getYAxis();
        if (Array.isArray(yAxis)) {
            return yAxis[0];
        }
        return yAxis;
    }

    /**
     * Same reason we override getYAxis: Disable double axis for scatter plot.
     */
    protected override handleSeriesDoubleYAxis(
        series: EChartsSerie[],
    ): EChartsSerie[] {
        return series;
    }
}
